import React, { useEffect, useRef, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Slider } from "@/components/ui/slider";

export const STEMS = [["talk", "Explanations"], ["count", "Counting"], ["click", "Clicks"], ["clap", "Claps & notes"]];
const KEY = "jt-countalong-mix";
const FULL = { talk: 1, count: 1, click: 1, clap: 1 };
const loadLevels = () => {
  try { return { ...FULL, ...JSON.parse(localStorage.getItem(KEY) || "{}") }; } catch { return { ...FULL }; }
};
const isFull = (l) => STEMS.every(([k]) => l[k] === 1);

// The video plays its own sound until a learner changes the mix; only then do the four separate tracks take over,
// and only once they are confirmed playing, so the sound can never just cut out.
export const useStemMix = (videoRef, name, version) => {
  const [levels, setLevels] = useState(loadLevels);
  const [ready, setReady] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const levelsRef = useRef(levels);
  const ctl = useRef({});

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const audios = Object.fromEntries(STEMS.map(([k]) => {
      const a = new Audio(`/media/intros/${name}.${k}.m4a?v=${version}`);
      a.preload = "metadata"; a.preservesPitch = true;
      return [k, a];
    }));
    const gains = {};
    let loaded = 0, failed = false, ctx = null, master = null, videoGain = null, on = false;
    const list = Object.values(audios);
    const fail = () => { failed = true; stop(); setUnavailable(true); };
    const engine = () => {
      if (ctx || failed) return;
      try {
        ctx = new (window.AudioContext || window.webkitAudioContext)();
        master = ctx.createGain(); master.gain.value = v.muted ? 0 : v.volume; master.connect(ctx.destination);
        STEMS.forEach(([k]) => {
          gains[k] = ctx.createGain(); gains[k].gain.value = levelsRef.current[k];
          ctx.createMediaElementSource(audios[k]).connect(gains[k]).connect(master);
        });
        ctx.resume().catch(() => {});
      } catch { fail(); }
    };
    const routeVideo = () => {
      if (videoGain || !ctx || ctx.state !== "running") return;
      try {
        videoGain = ctx.createGain();
        ctx.createMediaElementSource(v).connect(videoGain).connect(ctx.destination); // must reach destination or Chrome stops the video's clock
      } catch { videoGain = null; }
    };
    const sync = (force) => list.forEach((a) => { if (force || Math.abs(a.currentTime - v.currentTime) > 0.08) a.currentTime = v.currentTime; });
    function stop() {
      on = false;
      list.forEach((a) => a.pause());
      if (videoGain) videoGain.gain.value = 1;
    }
    const wanted = () => !failed && loaded === STEMS.length && !isFull(levelsRef.current);
    const start = () => {
      if (!wanted() || v.paused) return;
      engine();
      if (!ctx) return;
      on = true;
      sync(true);
      if (!videoGain || videoGain.gain.value !== 0) master.gain.value = 0; // stay silent until the swap, so nothing doubles up
      ctx.resume().catch(() => {});
      Promise.all(list.map((a) => { a.playbackRate = v.playbackRate; return a.play(); }))
        .then(() => (ctx.state === "running" ? null : Promise.race([ctx.resume(), new Promise((r) => setTimeout(r, 1500))])))
        .then(() => {
          if (!on) return;
          routeVideo();
          if (videoGain && ctx.state === "running") { videoGain.gain.value = 0; master.gain.value = v.muted ? 0 : v.volume; sync(true); }
          else fail(); // the browser won't let us mix, so keep the video's own sound
        })
        .catch(fail);
    };
    const pause = () => { if (on) list.forEach((a) => a.pause()); };
    ctl.current = {
      changed: () => {
        if (!wanted()) { stop(); return; }
        engine(); // runs inside the slider gesture, which browsers need before sound can start
        STEMS.forEach(([k]) => { if (gains[k]) gains[k].gain.value = levelsRef.current[k]; });
        if (!on) start();
      },
    };
    const handlers = {
      play: () => { if (wanted()) engine(); },
      playing: () => { if (on) { sync(true); list.forEach((a) => a.play().catch(fail)); } else start(); },
      pause, waiting: pause, ended: pause,
      seeked: () => { if (on) sync(true); },
      timeupdate: () => { if (on) sync(false); },
      ratechange: () => list.forEach((a) => { a.playbackRate = v.playbackRate; }),
      volumechange: () => { if (master) master.gain.value = v.muted ? 0 : v.volume; },
    };
    list.forEach((a) => {
      a.addEventListener("loadedmetadata", () => { loaded += 1; if (loaded === STEMS.length && !failed) setReady(true); }, { once: true });
      a.addEventListener("error", () => { failed = true; setReady(false); }, { once: true });
    });
    Object.entries(handlers).forEach(([e, f]) => v.addEventListener(e, f));
    return () => {
      Object.entries(handlers).forEach(([e, f]) => v.removeEventListener(e, f));
      list.forEach((a) => { a.pause(); a.src = ""; });
      ctl.current = {};
      if (ctx && !videoGain) ctx.close().catch(() => {});
    };
  }, [videoRef, name, version]);

  const setLevel = (k, val) => {
    const next = { ...levelsRef.current, [k]: val };
    levelsRef.current = next;
    setLevels(next);
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* storage blocked */ }
    ctl.current.changed?.();
  };
  const reset = () => { STEMS.forEach(([k]) => { levelsRef.current = { ...levelsRef.current, [k]: 1 }; }); setLevels({ ...FULL }); try { localStorage.setItem(KEY, JSON.stringify(FULL)); } catch { /* storage blocked */ } ctl.current.changed?.(); };
  return { levels, setLevel, reset, ready, unavailable };
};

export const MixSliders = ({ mix, testId }) => {
  if (!mix.ready) return null;
  return (
    <div className="border-t border-primary/20 px-4 py-3" data-testid={`${testId}-mix`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm text-muted-foreground"><SlidersHorizontal size={14} className="text-primary" /> Sound mix</p>
        {!isFull(mix.levels) && <button type="button" onClick={mix.reset} data-testid={`${testId}-mix-reset`} className="text-xs text-primary hover:underline">Reset to full</button>}
      </div>
      {mix.unavailable ? (
        <p className="mt-2 text-xs text-muted-foreground" data-testid={`${testId}-mix-unavailable`}>The sound mix doesn't work in this browser, so the video plays its normal sound.</p>
      ) : (
        <div className="mt-2 grid gap-x-6 gap-y-3 sm:grid-cols-2">
          {STEMS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-3 text-xs">
              <span className="w-24 shrink-0 font-semibold">{label}</span>
              <Slider value={[Math.round(mix.levels[k] * 100)]} max={100} step={5} onValueChange={([x]) => mix.setLevel(k, x / 100)}
                aria-label={`${label} volume`} data-testid={`${testId}-mix-${k}`} className="flex-1" />
              <span className="w-9 text-right font-mono text-muted-foreground" data-testid={`${testId}-mix-${k}-value`}>{Math.round(mix.levels[k] * 100)}%</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
